import { useSearchParams } from 'next/navigation';
import { type KeyboardEvent, useRef, useState } from 'react';

import { stripHtml } from '@libs/common-next';

import type { AssistedSearchState, SearchItem } from '../search.types';

const EMPTY_ASSISTED_SEARCH: AssistedSearchState = {
    searchTerm: '',
    searchAssistTerm: '',
    searchAssistName: '',
    isModified: false,
};

function getSearchAssistName(item: SearchItem, query: string) {
    if (item.kind === 'recent') return 'recent searches';
    if (item.kind === 'product') return 'product results';
    return query === '' ? 'popular searches' : '';
}

export function useSearchQueryState() {
    const params = useSearchParams();
    const [query, setQueryState] = useState(stripHtml(params?.get('q') ?? ''));
    const currentQueryRef = useRef(query);
    const [assistedSearch, setAssistedSearch] = useState<AssistedSearchState>(EMPTY_ASSISTED_SEARCH);

    const setQuery = (nextQuery: string) => {
        currentQueryRef.current = nextQuery;
        setQueryState(nextQuery);
    };

    const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') return;
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') return;

        const inputValue = event.currentTarget.value;

        if (inputValue === '') {
            setAssistedSearch(EMPTY_ASSISTED_SEARCH);
        } else if (assistedSearch.searchTerm !== '' && assistedSearch.searchTerm !== inputValue) {
            setAssistedSearch(current => ({ ...current, isModified: true }));
        }
    };

    const captureSearchSelection = (item: SearchItem) => {
        setAssistedSearch({
            searchTerm: item.label,
            searchAssistTerm: query,
            searchAssistName: getSearchAssistName(item, query),
            isModified: false,
        });
    };

    const completeSearchSelection = (item: SearchItem) => {
        if (item.kind !== 'product') setQuery(item.label);
    };

    const clearQuery = () => {
        setQuery('');
    };

    return {
        assistedSearch,
        captureSearchSelection,
        clearQuery,
        completeSearchSelection,
        getCurrentQuery: () => currentQueryRef.current,
        onInputKeyDown,
        query,
        setQuery,
    };
}
